import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import isHotkey from 'is-hotkey';
import { keys } from 'lodash-es';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { message } from 'src/common/components/message';
import { useCloseModal, useIsExistModalId, useOpenModal } from 'src/common/components/next-modal';
import { IS_WINDOWS } from 'src/common/utils/environment';
import { globalListenerHelper } from 'src/common/utils/global-listener-helper';
import { useOpenMoveTo } from 'src/components/move-to';
import { useOpenDeleteSyncBlockDialog } from 'src/components/unsync';
import { useCopyTableItem } from 'src/editor/editor/plugin/table/hook';
import { AIEditorFrom, AIEditorScene } from 'src/editor/editor/uikit/ai-editor/const';
import { useOpenAIEditor } from 'src/editor/editor/uikit/ai-editor/use-open-ai-editor';
import { getEditorModelByEditorKey } from 'src/editor/editor/uikit/editable-models';
import { useOpenBlockRightMenuList } from 'src/editor/editor/uikit/hover-menu/block-menu-list/hook';
import {
  useCreateBlockAndOpenMenu,
  useShortcutKeyInsertBlock,
} from 'src/editor/editor/uikit/hover-menu/hook';
import { useCopyBlockRecord } from 'src/hooks/block/use-copy-block-record';
import { useDeleteSelectedTableCells } from 'src/hooks/block/use-delete-selected-table-calls';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { lockScreen } from 'src/hooks/public/use-lock';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { useTransaction } from 'src/hooks/use-transaction';
import { Modals } from 'src/modals';
import { HISTORY_REDO, HISTORY_UNDO, TRANSACTION_FIRE } from 'src/redux/actions';
import { addBlock } from 'src/redux/managers/block/add';
import { archiveBlock } from 'src/redux/managers/block/archive';
import { updateBlock } from 'src/redux/managers/block/update';
import { useCloseBlockMenuList } from 'src/redux/managers/ui';
import { isLikeFoldBlock, patchExpand } from 'src/redux/managers/ui/use-fold';
import { simpleTableActions } from 'src/redux/reducers/simple-table';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock, SelectBlock } from 'src/redux/types';
import { $appUiStateCache, useSetIsEditingPage } from 'src/services/app';
import { $networkStatus } from 'src/services/network-status';
import { $currentUserCache } from 'src/services/user/current-user';
import {
  isCollection,
  isEditableType,
  isLinkPageBlock,
  isMindMap,
  isPageLike,
} from 'src/utils/block-type-utils';
import {
  filterDescendantId,
  getNextSiblingBlockId,
  getPreSiblingBlockId,
  getRefBlock,
  getSyncBlockBackLinkInfo,
} from 'src/utils/block-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { focusEditable } from 'src/utils/editor';
import { getBlockNodeFromEditorKey, getEditorKey } from 'src/utils/editor-utils';
import { isFlowUsApp } from 'src/utils/electron-util';
import { getDynamicPageId, judgeSharePage } from 'src/utils/getPageId';
import { getLocationOrigin } from 'src/utils/location-utils';
import { idsToSelectedBlocks, selectedBlocksToIds } from 'src/utils/select-block-util';
import { useOpenNewDiscussionPopup } from 'src/views/comments/use-open-new-discussion-popup';
import { useToggleCollapse } from 'src/views/main/aside/toc-trigger';
import { GlobalSearchType, useOpenSearchPopup } from 'src/views/main/header/search';
import { useCreateDocPage } from '../use-create-page';
import { useSelectedBlockHistory } from '../use-selected-block-history';
import { useAlignKey } from './use-align-key';
import { useArrowKey } from './use-arrow-key';
import { useChangeColorKey } from './use-change-color-key';
import { useConvertKey } from './use-convert-key';
import { useLastColor } from './use-last-color';
import { useModEnterKey } from './use-mod-enter-key';
import { useRichTextShortCut } from './use-rich-text-shortcut';
import { useRightPageKey } from './use-right-page-key';
import { useTabKey } from './use-tab-key';

/**
 * NOTE: 加快捷键的时候注意要区别是否影响到【思维导图】
 * 如果不应用到思维导图的话，需要加 isMindMapPage 的判断，具体可以搜一下这个变量在这里的是怎么用的
 *
 * 另外，这里有些快捷键在思维导图下是不会执行的，比如enter，因为在 useMindMapShortCut 里已经拦截掉自己处理了。
 */
export const usePageShortcut = (readonly?: Boolean) => {
  const openModal = useOpenModal();
  const history = useHistory();
  const convertKey = useConvertKey();
  const { selectedBlockHistory } = useSelectedBlockHistory();
  const transaction = useTransaction();
  const closeBlockMenuList = useCloseBlockMenuList();
  const arrowKey = useArrowKey();
  const copyBlockRecord = useCopyBlockRecord();
  const openBlockRightMenuList = useOpenBlockRightMenuList();
  const openMoveTo = useOpenMoveTo();
  const tabKey = useTabKey();
  const openDeleteSyncBlockDialog = useOpenDeleteSyncBlockDialog();
  const toggleCollapse = useToggleCollapse();
  const openNewDiscussionPopup = useOpenNewDiscussionPopup();
  const deleteSelectedTableCells = useDeleteSelectedTableCells();
  const rightPageKey = useRightPageKey();
  const createBlockAndOpenMenu = useCreateBlockAndOpenMenu();
  const shortcutKeyInsetBlock = useShortcutKeyInsertBlock();
  const createPage = useCreateDocPage();
  const openSearchPopup = useOpenSearchPopup();
  const modEnterKey = useModEnterKey();
  const closeModal = useCloseModal();
  const changeColorKey = useChangeColorKey();
  const alignKey = useAlignKey();
  const existSearchModal = useIsExistModalId(Modals.QUICK_SEARCH);
  const richTextShortcut = useRichTextShortCut();
  const copyTableItem = useCopyTableItem();
  const lastColor = useLastColor();
  const openAIEditor = useOpenAIEditor();
  const setIsEditingPage = useSetIsEditingPage();

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (isHotkey('alt+shift+l')(event)) {
        event.preventDefault();
        lockScreen();
      }

      // record 页面编辑属性时禁用全选 | 弹窗时有输入框的场景
      if (
        ['input', 'textarea'].includes(document.activeElement?.tagName?.toLowerCase() ?? '') &&
        !(event.target as HTMLElement).closest('.CodeMirror')
      ) {
        let isMindMap = false;
        if (document.activeElement instanceof HTMLElement) {
          if (document.activeElement.closest('.hidden-textarea')) {
            isMindMap = true;
          }
        }
        // 如果选中了一个导图节点，则isMindMap为true,此时需要继续响应快捷键,其他情况直接return(原逻辑)
        if (!isMindMap) {
          return;
        }
      }
      if (document.activeElement?.closest('.disable-short-cut')) {
        return;
      }

      // 设置弹窗 弹起时禁掉
      if (
        document.querySelector('[data-modal-id="SETTING"]') ||
        document.activeElement?.matches('.biz-inline-editable')
      ) {
        return;
      }
      if (document.activeElement instanceof HTMLElement) {
        if (
          // 思维导图节点编辑时不响应全局事件
          document.activeElement.isContentEditable === true &&
          document.activeElement.closest('[data-mind-map-id]')
        ) {
          return;
        }
      }
      arrowKey(event); // 分享页也支持方向键
      lastColor(event);
      rightPageKey(event);

      const pageId = getDynamicPageId();

      if (!readonly && !cache.blocks[pageId]?.data.format?.locked) {
        // mod+opt/shift+[0-9]
        convertKey(event, pageId);
        changeColorKey(event);
        richTextShortcut(event, false);
        tabKey(event);
        alignKey(event);

        const pageBlock = cache.blocks[pageId];
        if (!pageBlock) return;
        const isMindMapPage = isMindMap(pageBlock.type);
        if (isHotkey('mod+a')(event)) {
          let viewId: string | undefined = undefined;
          if (isCollection(pageBlock.type) && location.hash.startsWith('#')) {
            viewId = location.hash.slice(1);
          }
          const selectedBlocks: SelectBlock[] = [];
          const loop = (subNodes: string[]) => {
            for (const id of subNodes) {
              const block = cache.blocks[id];
              if (block) {
                if (block.type === BlockType.COLUMN_LIST || block.type === BlockType.COLUMN) {
                  loop(block.subNodes);
                } else {
                  selectedBlocks.push({
                    blockId: id,
                    viewId,
                  });
                  if (isMindMapPage) {
                    loop(block.subNodes);
                  }
                }
              }
            }
          };
          if (isMindMapPage) {
            loop([pageBlock.uuid]);
          } else {
            loop(pageBlock.subNodes);
          }
          dispatch(uiActions.updateSelectBlocks(selectedBlocks));
          event.preventDefault();
          window.getSelection().removeAllRanges();
          document.dispatchEvent(new Event('_clearTextSelection'));
          (document.activeElement as HTMLInputElement).blur();
        }

        if (isHotkey('mod+shift+u')(event)) {
          const block = cache.blocks[pageId];
          if (!block) return;
          const { parentId, spaceId } = block;
          if (parentId !== spaceId) {
            history.push(`/${getOwnerPage(pageId)}`);
          }
        }

        if (isHotkey('mod+d')(event)) {
          event.preventDefault();

          if (cache.simpleTable.chosenCells) {
            copyTableItem();
            return;
          }

          const uuid = document.activeElement
            ?.closest('[data-block-id]')
            ?.getAttribute('data-block-id');

          if (uuid === pageId) return;

          const syncId = document.activeElement
            ?.closest('[data-sync-id]')
            ?.getAttribute('data-sync-id');
          const viewId = document.activeElement
            ?.closest('[data-block-id]')
            ?.getAttribute('data-view-id');

          const { selectedBlocks } = cache.ui;
          const blocks = selectedBlocks.length
            ? selectedBlocks
            : uuid
            ? idsToSelectedBlocks(uuid, viewId ?? undefined, syncId ?? undefined)
            : [];

          if (
            blocks.some((selectedBlock) => {
              const _block = cache.blocks[selectedBlock.blockId];
              if (!_block) return true;
              return getPermissions(_block.parentId).role === PermissionRole.READER;
            })
          ) {
            return;
          }

          void copyBlockRecord(blocks, undefined, blocks.find((v) => v.syncId)?.syncId);
          (document.activeElement as HTMLInputElement).blur();
          closeBlockMenuList();

          return;
        }

        if (isHotkey('mod+.')(event)) {
          // mod+./mod+。这里是识别不出来的，看event也看不出差异，因此两个快捷键都支持
          const uuid = document.activeElement
            ?.closest('[data-block-id]')
            ?.getAttribute('data-block-id');
          if (!uuid) return;
          if (uuid === pageId) return;
          if (cache.ui.selectedBlocks.length > 0) return;
          event.preventDefault();
          const syncId =
            document.activeElement?.closest('[data-sync-id]')?.getAttribute('data-sync-id') ??
            undefined;
          const viewId =
            document.activeElement?.closest('[data-block-id]')?.getAttribute('data-view-id') ??
            undefined;
          dispatch(
            uiActions.updateSelectBlocks([
              {
                blockId: uuid,
                syncId,
                viewId,
              },
            ])
          );
        }

        if (isHotkey(['Backspace', 'Delete'], { byKey: true })(event)) {
          const { role } = getPermissions(pageId);
          if (role === PermissionRole.READER || role === PermissionRole.NONE) {
            return;
          }
          if (cache.ui.selectedCells.length) {
            // 多维表的 cell 自己处理, 这里是简单表格
            const firstCell = cache.ui.selectedCells[0];
            if (firstCell) {
              const { viewId } = firstCell;
              const block = cache.blocks[viewId];
              if (block?.type === BlockType.TABLE) {
                deleteSelectedTableCells();
              }
            }
            return;
          }
          // 使用 filterSelectedBlocks可减少发op的个数
          const filterSelectedBlocks = filterDescendantId(cache.ui.selectedBlocks);
          if (filterSelectedBlocks.length > 0) {
            if (
              filterSelectedBlocks.some(
                (selectBlock) => getPermissions(selectBlock.blockId).role === PermissionRole.READER
              )
            ) {
              return;
            }

            const { selectedBlocks } = cache.ui;
            const deleteBlocks = () => {
              transaction(() => {
                archiveBlock(selectedBlocksToIds(filterSelectedBlocks));
                selectedBlockHistory([]);
              });
              closeBlockMenuList();
            };
            const backLinkInfo = getSyncBlockBackLinkInfo(selectedBlocks);
            if (backLinkInfo) {
              openDeleteSyncBlockDialog(backLinkInfo.onlyOne, () => {
                deleteBlocks();
              });
              return;
            }
            let nextSelectBlockId;
            if (isMindMapPage) {
              nextSelectBlockId = getNextSiblingBlockId(
                selectedBlocks[selectedBlocks.length - 1]?.blockId ?? ''
              );
              if (!nextSelectBlockId) {
                nextSelectBlockId = getPreSiblingBlockId(selectedBlocks[0]?.blockId ?? '');
              }
              if (!nextSelectBlockId) {
                nextSelectBlockId = cache.blocks[selectedBlocks[0]?.blockId ?? '']?.parentId;
              }
              // 如果当前是思维导图并且focus在代码块，则不能删除
              const codeBlockFocused = document.activeElement?.closest('.CodeMirror');
              if (codeBlockFocused) return;
            }
            event.preventDefault();
            deleteBlocks();
            if (nextSelectBlockId) {
              selectedBlockHistory(idsToSelectedBlocks([nextSelectBlockId]));
            }
          }
        }

        if (isHotkey('Enter')(event)) {
          if ($appUiStateCache.$blockMenuListId) return;
          const firstBlock = cache.ui.selectedBlocks[0];
          const uuid = firstBlock?.blockId;
          if (uuid) {
            const block = cache.blocks[uuid];
            if (block) {
              const parentBlock = cache.blocks[block.parentId];
              if (parentBlock && isCollection(parentBlock.type)) {
                dispatch(
                  uiActions.updateSelectCells([
                    {
                      recordId: uuid,
                      propertyId: 'title',
                      viewId: firstBlock.viewId ?? '',
                      groupValue: Object.keys(firstBlock.groupValues ?? {})?.[0],
                    },
                  ])
                );
              } else if (block.type === BlockType.TABLE) {
                const recordId = block.subNodes[0];
                const propertyId = block.data.format?.tableBlockColumnOrder?.[0];
                if (recordId && propertyId) {
                  dispatch(
                    uiActions.update({
                      selectedCells: [
                        {
                          recordId,
                          propertyId,
                          viewId: block.uuid,
                          groupValue: Object.keys(firstBlock.groupValues ?? {})?.[0],
                        },
                      ],
                    })
                  );
                  dispatch(simpleTableActions.update({ focus: true }));
                  focusEditable(getEditorKey(block.uuid), Infinity, Infinity);
                }
              } else if (isEditableType(block.type)) {
                focusEditable(firstBlock.editorKey || getEditorKey(uuid), Infinity, Infinity);
              } else {
                transaction(() => {
                  const newId = addBlock({}, { parentId: block.parentId, after: uuid });
                  selectedBlockHistory([firstBlock]);
                  focusEditable(newId, Infinity, Infinity);
                });
              }
              dispatch(uiActions.updateSelectBlocks([]));
            }
          }
        }

        if (isHotkey('mod+/')(event)) {
          if (!$appUiStateCache.$blockMenuListId) {
            if (cache.ui.selectedBlocks.length) {
              const blockId = cache.ui.selectedBlocks[0]?.blockId as string;
              const blockNode = document.querySelector(`[data-block-id="${blockId}"]`);

              if (blockNode) {
                openBlockRightMenuList({
                  blockId,
                  blockNode: blockNode as HTMLElement,
                  placement: 'bottom-start',
                  offset: [0, 8],
                });
              }
            } else {
              const editorKey = document.activeElement?.getAttribute('data-editor');
              const node = document.activeElement?.closest('[data-block-id');
              if (!(node instanceof HTMLElement)) return;
              const { blockId } = node.dataset;
              if (!blockId) return;

              let rect: DOMRect | null | undefined =
                document.activeElement?.getBoundingClientRect();
              if (editorKey) {
                const model = getEditorModelByEditorKey(editorKey);
                if (model && model.selection) {
                  rect = model.getBoundingClientRectOfRange(
                    model.selection.focusOffset,
                    model.selection.focusOffset
                  );
                }
              }
              if (rect) {
                openBlockRightMenuList({
                  blockId,
                  blockNode: {
                    getBoundingClientRect: () => rect,
                    dataset: (node as HTMLElement).dataset,
                  } as HTMLElement,
                  noSelect: true,
                  placement: 'bottom-start',
                  offset: [0, 8],
                });
              }
            }
          }
        }

        if (isHotkey('mod+shift+p')(event)) {
          event.preventDefault();
          if (cache.ui.selectedBlocks.length) {
            const uuid = cache.ui.selectedBlocks[0]?.blockId as string;
            const popcorn = document.querySelector(`[data-block-id="${uuid}"]`);
            if (popcorn) {
              openMoveTo({
                uuid,
                popcorn,
                placement: 'left-start',
              });
            }
          } else {
            // 产品需求：全局弹出 移动到 时只能移动左侧页面
            const popcorn = document.getElementById('page-header-more');
            if (popcorn) {
              openMoveTo({
                uuid: getDynamicPageId('left'),
                popcorn,
                placement: 'bottom-end',
              });
            }
          }
        }

        if (isHotkey(['a', 'b'])(event) && !isMindMapPage) {
          if (cache.ui.selectedBlocks.length > 0) {
            event.preventDefault();
            const keyA = isHotkey('a')(event);
            shortcutKeyInsetBlock({
              pos: keyA ? 'before' : 'after',
            });
            openModal.closeModal($appUiStateCache.$blockMenuListId);
          }
        }

        if (isHotkey(['f'])(event) && !isMindMapPage) {
          if (cache.ui.selectedBlocks.length > 0) {
            const blockId = cache.ui.selectedBlocks[0]?.blockId;
            if (!blockId) return;
            event.preventDefault();
            const block = cache.blocks[blockId];
            if (!block) return;
            const isLinkPage = isLinkPageBlock(block);
            const reallyBlockId = isLinkPage ? getRefBlock(blockId)?.uuid : blockId;
            const reallyBlock = cache.blocks[reallyBlockId ?? ''];
            if (isPageLike(reallyBlock?.type)) {
              void writeTextInClipboard(`${getLocationOrigin()}/${reallyBlockId}`);
            } else {
              void writeTextInClipboard(`${getLocationOrigin()}/${pageId}#${blockId}`);
            }
            openModal.closeModal($appUiStateCache.$blockMenuListId);
          }
        }

        if (isHotkey('mod+shift+m')(event)) {
          // 产品要求多选超过一个块时不响应快捷键
          if (cache.ui.selectedBlocks.length === 1) {
            event.preventDefault();
            const uuid = cache.ui.selectedBlocks[0]?.blockId as string;
            if (cache.blocks[uuid]?.type !== BlockType.REFERENCE) {
              openNewDiscussionPopup({ blockId: uuid });
            }
          }
        }

        modEnterKey(event);

        if (isHotkey('Esc')(event)) {
          const { selectedCells, selectedBlocks } = cache.ui;

          if (selectedCells.length > 0) {
            const recordIds = selectedCells.reduce((pre, current) => {
              pre.add(current.recordId);
              return pre;
            }, new Set<string>());
            dispatch(
              uiActions.updateSelectBlocks(
                idsToSelectedBlocks(
                  [...recordIds],
                  selectedCells.find((v) => v.viewId)?.viewId,
                  selectedCells.find((v) => v.syncId)?.syncId
                )
              )
            );
            return;
          }

          if (selectedBlocks.length) {
            dispatch(uiActions.updateSelectBlocks([]));
            return;
          }

          if (event.target) {
            const editorKey = (event.target as HTMLElement).getAttribute('data-editor');
            const blockNode = getBlockNodeFromEditorKey(editorKey);
            if (!blockNode) return;
            const { blockId, syncId } = blockNode.dataset;

            if (blockId && !document.querySelector('#dropDownContent')) {
              window.getSelection().removeAllRanges();
              document.dispatchEvent(new Event('_clearTextSelection'));
              (document.activeElement as HTMLInputElement).blur();
              dispatch(uiActions.updateSelectBlocks([{ blockId, syncId }]));
            }
          }

          return;
        }

        if (isHotkey('mod+z')(event)) {
          event.preventDefault();
          dispatch(HISTORY_UNDO());
        }

        if (isHotkey('mod+shift+z')(event)) {
          event.preventDefault();
          dispatch(HISTORY_REDO());
        }

        if (isHotkey('mod+j')(event)) {
          let maxIndex = 0;
          const page = cache.blocks[pageId];
          const subNodes = page?.subNodes ?? [];
          cache.ui.selectedBlocks.forEach(({ blockId }) => {
            maxIndex = Math.max(subNodes.indexOf(blockId), maxIndex);
          });
          // 虚拟列表会有问题
          const blockId = subNodes[maxIndex];
          if (!blockId) return;
          const blockNode = document.querySelector(
            `[data-block-id="${blockId}"]`
          ) as HTMLElement | null;
          if (!blockNode) return;
          openAIEditor({
            blockId,
            popcorn: blockNode,
            editorScene: AIEditorScene.BlockSelected,
            from: AIEditorFrom.BlockMenu,
          });
          closeBlockMenuList();
        }
      }
      if (!readonly && isHotkey('mod+l')(event)) {
        event.preventDefault();
        const page = cache.blocks[pageId];
        const locked = page?.data.format?.locked;
        transaction(() => {
          updateBlock(pageId, {
            data: {
              format: {
                locked: !locked,
                lockedBy: $currentUserCache.uuid,
              },
            },
          });
        });
        setIsEditingPage(false);
      }
      // #region 全模式通用快捷键
      if (isHotkey('mod+opt+t')(event)) {
        event.preventDefault();
        const page = cache.blocks[pageId];
        const allFoldList: string[] = [];

        const loop = (block?: NextBlock) => {
          if (!block) return;
          if (isLikeFoldBlock(block.type)) {
            allFoldList.push(block.uuid);
          }
          block.subNodes.forEach((id) => {
            loop(cache.blocks[id]);
          });
        };
        loop(page);
        if (allFoldList.length > 0) {
          if (keys($appUiStateCache.$expandFoldRecord).length) {
            const hasOpen = allFoldList.some((i) => $appUiStateCache.$expandFoldRecord[i]);
            patchExpand(allFoldList, !hasOpen);
          } else {
            patchExpand(allFoldList, false);
          }
        }
      }

      if (isHotkey('mod+s')(event)) {
        event.preventDefault();
        if (!readonly) {
          if ($networkStatus.offline) {
            message.warning('当前处于断网状态，联网后将自动保存至云端');
          } else {
            dispatch(TRANSACTION_FIRE());
            message.success({ key: 'save', content: '已自动保存至云端，无需手动保存' });
          }
        }
        return true;
      }

      if (isHotkey(['mod+\\', 'mod+、'])(event)) {
        event.preventDefault();
        toggleCollapse(!$appUiStateCache.$isCollapsed);
      }

      if (!isFlowUsApp.check) {
        const isCodeMirror = (event.target as HTMLElement).closest('.CodeMirror');
        if (IS_WINDOWS && !isCodeMirror) {
          // 代码块跟这个快捷键冲突了
          if (isHotkey('mod+[')(event)) {
            history.goBack();
          }

          if (isHotkey('mod+]')(event)) {
            history.goForward();
          }
        }
      }
      // #endregion
    };

    const handleKeydownCapture = (event: KeyboardEvent) => {
      // safari 的 esc 是 浏览器退出全屏
      if (isHotkey('Esc')(event) && event.bubbles) {
        // 阻止 safari 退出全屏
        event.preventDefault();
      }
      // 全局无论任何时候mod+p都弹搜索框出来
      if (isHotkey('mod+p')(event)) {
        event.preventDefault();
        if (existSearchModal) {
          closeModal(Modals.QUICK_SEARCH);
        } else {
          if (judgeSharePage()) {
            openSearchPopup({
              type: GlobalSearchType.share,
              inPageId: getDynamicPageId(),
            });
          } else {
            openSearchPopup();
          }
        }
      }
    };

    globalListenerHelper.addEventListener('keydown', handleKeydown);
    document.addEventListener('keydown', handleKeydownCapture, true);

    return () => {
      globalListenerHelper.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('keydown', handleKeydownCapture, true);
    };
  }, [
    arrowKey,
    closeBlockMenuList,
    convertKey,
    copyBlockRecord,
    history,
    openBlockRightMenuList,
    openDeleteSyncBlockDialog,
    openMoveTo,
    openNewDiscussionPopup,
    readonly,
    selectedBlockHistory,
    tabKey,
    toggleCollapse,
    transaction,
    deleteSelectedTableCells,
    rightPageKey,
    createBlockAndOpenMenu,
    shortcutKeyInsetBlock,
    createPage,
    openSearchPopup,
    openModal,
    modEnterKey,
    changeColorKey,
    existSearchModal,
    closeModal,
    richTextShortcut,
    alignKey,
    copyTableItem,
    lastColor,
    openAIEditor,
    setIsEditingPage,
  ]);
};
